import { client } from "@/lib/client";
import { queryClient } from "@/lib/react-query";

export const useAuth = () => {
  const login = async (email: string, password: string) => {
    if (!email || !password) {
      throw new Error("Email and password are required");
    }

    try {
      const uri = `${client.baseUrl}/auth/login`;

      const response = await fetch(uri, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to login");
      }

      queryClient.resetQueries();
    } catch (error) {
      console.error(error);
      throw new Error("auth.login-error");
    }
  };

  const googleLogin = () => {
    window.location.href = `${client.baseUrl}/auth/google/login`;
  };

  const googleSignup = () => {
    window.location.href = `${client.baseUrl}/auth/google/signup`;
  };

  const signup = async (
    email: string,
    password: string,
    confirmPassword: string
  ) => {
    if (!email || !password || !confirmPassword) {
      throw new Error("Email, password and confirmation password are required");
    }

    if (password !== confirmPassword) {
      throw new Error("Passwords do not match");
    }

    const uri = `${client.baseUrl}/auth/signup`;

    const response = await fetch(uri, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      throw new Error("Failed to sign up");
    }

    await login(email, password);
  };

  const resetPassword = async (email: string) => {
    try {
      const uri = `${client.baseUrl}/auth/password/forgot`;

      const response = await fetch(uri, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error("Failed to send password reset email");
      }
    } catch (e) {
      throw e;
    }
  };

  const sendEmailVerification = async () => {
    const uri = `${client.baseUrl}/auth/send-email-verification`;

    const response = await fetch(uri, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to send email verification");
    }
  };

  const logout = async () => {
    try {
      await client.logout();
    } finally {
      queryClient.resetQueries();
      queryClient.clear();
      queryClient.removeQueries();
    }
  };

  const deleteAccount = async () => {
    const uri = `${client.baseUrl}/auth/delete-account`;

    const response = await fetch(uri, {
      method: "POST",
    });

    if (!response.ok) {
      throw new Error("Failed to delete account");
    }

    await logout();
  };

  return {
    login,
    googleLogin,
    googleSignup,
    signup,
    logout,
    resetPassword,
    sendEmailVerification,
    deleteAccount,
  };
};

